import { useId } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * A marca da IBFT — o disco laranja com a folha vazada.
 *
 * **Vetor desenhado, não o PNG do perfil.** A marca aparece a 32px na coluna
 * lateral e a 16px na aba do navegador; um bitmap de rede social recortado
 * chega nesses tamanhos como uma mancha, e ainda pediria `@2x`/`@3x` para as
 * telas retina de quem abre o painel no celular. Em SVG são ~700 bytes que
 * ficam nítidos em qualquer densidade e não custam uma requisição.
 *
 * **O laranja mora aqui e só aqui.** Ele não é token de cor em
 * `styles/index.css` de propósito (ver o comentário de `--primary` lá): a cor
 * da marca é um ativo da igreja, não um papel do sistema de design. Token
 * convida a pintar botão; hex dentro do componente da marca, não.
 *
 * `aria-hidden` sempre: quem usa a marca é quem tem um nome acessível melhor
 * do que ela ("Gestão IBFT", no link da sidebar). Marca decorativa dentro de
 * link nomeado é o caso em que o leitor de tela não deve dizer nada a mais.
 */

interface BrandMarkProps {
  className?: string;
}

export function BrandMark({ className }: BrandMarkProps) {
  // Dois `BrandMark` na mesma página (sidebar recolhida + faixa superior)
  // colidiriam no `id` do gradiente, e o segundo apagaria o primeiro — o
  // navegador resolve `url(#…)` pelo documento inteiro, não pelo componente.
  // Os dois-pontos que o React usa no `useId` são válidos em `id`, mas quebram
  // o seletor de `url()` em parte dos navegadores.
  const gradientId = `brand-${useId().replace(/:/g, '')}`;

  return (
    <svg
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
      className={cn('size-8', className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F2823C" />
          <stop offset="0.45" stopColor="#EA6326" />
          <stop offset="1" stopColor="#D9440F" />
        </linearGradient>
      </defs>

      <circle cx="50" cy="50" r="50" fill={`url(#${gradientId})`} />

      {/* O quadrado é girado, não desenhado torto: manter os cantos
          arredondados como um `rx` real é o que preserva o raio uniforme em
          qualquer tamanho. Um caminho "já girado" distorceria os cantos. */}
      <g transform="rotate(-11.5 51 48)">
        <path
          fill="#F8F3EF"
          d="M37 25h28a9 9 0 0 1 9 9v28a9 9 0 0 1-9 9H37a9 9 0 0 1-9-9V34a9 9 0 0 1 9-9z"
        />
        {/* As duas lâminas que vazam o quadrado. Pintadas com o mesmo gradiente
            do disco, e não com um laranja chapado: chapado, o vazado apareceria
            como um adesivo colado por cima em vez de um recorte. */}
        <g fill={`url(#${gradientId})`}>
          <path d="M74 25c-23 10-39 26-46 46h-3V57c9-15 26-26 49-32z" />
          <path d="M28 71c4-13 11-23 22-30-7 9-11 19-13 30z" />
        </g>
      </g>
    </svg>
  );
}
